'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tiny shared state holder for inline "toggle failed" feedback on
 * island buttons (Enroll, Subscribe, Wishlist, WatchLater, ReviewComposer).
 *
 * <p>Islands ship into host pages that may or may not provide a global
 * toast system. We therefore render error feedback *inline* (under the
 * button) rather than depending on a host-side notification provider.
 *
 * <p>Behaviour:
 * <ul>
 *   <li>{@code setError(msg)} stores a message and starts a 4s auto-clear
 *       timer.</li>
 *   <li>{@code clearError()} clears immediately (e.g. when the user
 *       re-clicks to retry).</li>
 *   <li>Calling {@code setError} again before the timer fires resets
 *       the timer — the user always sees the latest message for at
 *       least the auto-dismiss window.</li>
 *   <li>The timer is cleared on unmount to avoid setState-after-unmount.</li>
 * </ul>
 */
export function useToggleErrorMessage(autoClearMs = 4000): {
  error: string | null;
  setError: (msg: string) => void;
  clearError: () => void;
} {
  const [error, setErrorState] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearError = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setErrorState(null);
  }, []);

  const setError = useCallback(
    (msg: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setErrorState(msg);
      timerRef.current = setTimeout(() => {
        setErrorState(null);
        timerRef.current = null;
      }, autoClearMs);
    },
    [autoClearMs],
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return { error, setError, clearError };
}
